/* eslint-disable react/jsx-key */
import React, { createRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTable, useSortBy, usePagination, useFilters } from 'react-table';
import classNames from 'classnames';

import { TableCaption } from './TableCaption';
import { TableHead } from './TableHead';
import { TableBody } from './TableBody';
import { NextPageButton } from './NextPageButton';
import { useTableSortData } from '../hooks';
import { UPDATE_SORT } from '../../../../../../store/js/modules/statspro/stats-leaders/actions';
import { statsLeadersSelector } from '../../../../../../store/js/modules/statspro/stats-leaders/selectors/simple-selectors';
import { useIsScrollable } from '../../../../../../../../common/react/hooks/use-is-scrollable';
import { TableScrollButtons } from './TableScrollButtons';
import { statsLeadersFiltersSelector } from '../../../../../../store/js/modules/statspro/stats-leaders/selectors';
import { FIRST_SORTABLE_COLUMN_INDEX } from '../../config/stats';

const PAGE_SIZE = 20;
const SCROLL_DEBOUNCE_MS = 100;

export const Table = ( { columns, data, players } ) => {
    const dispatch = useDispatch();

    // ===== State Props ==================================================== //

    const sort = useSelector( statsLeadersSelector ).sort;
    const filters = useSelector( statsLeadersFiltersSelector );

    // ===== Refs =========================================================== //

    const tableRef = createRef();
    const scrollWrapperRef = createRef();

    // ===== Local State ==================================================== //

    const [ scrollPosition, setScrollPosition ] = useState( 0 );
    
    // ===== Data Hooks ===================================================== //

    const isTableScrollable = useIsScrollable( scrollWrapperRef );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        // @ts-ignore
        preFilteredRows,
        // @ts-ignore
        page,
        // @ts-ignore
        canNextPage,
        // @ts-ignore
        setPageSize,
        // @ts-ignore
        state: { pageSize }
    } = useTable(
        {
            columns,
            data,
            initialState: {
                // @ts-ignore
                pageSize: PAGE_SIZE,
                sortBy: [
                    {
                        id: sort.columnId ?? columns[ FIRST_SORTABLE_COLUMN_INDEX ].accessor,
                        desc: sort.sortDirection === 'descending'
                    }
                ],
                filters: [
                    {
                        id: 'position',
                        value: filters.positions
                    },
                    {
                        id: 'team',
                        value: filters.teams
                    }
                ]
            },
            disableSortRemove: true
        },
        useFilters,
        useSortBy,
        usePagination
    );

    const {
        headerGroup,
        sortedColumn,
        sortDirection
    } = useTableSortData( headerGroups );

    // ===== Effects ======================================================== //

    useEffect( () => {
        if ( sortedColumn ) {
            dispatch(
                UPDATE_SORT.request( {
                    columnId: sortedColumn.id,
                    sortDirection
                } )
            );
        }
    }, [ sortedColumn, sortDirection ] );

    /**
     * Apply filters on change
     */
    useEffect( () => {
        const headers = headerGroups[ 0 ].headers;

        headers
            .find( col => col.id === 'position' )
            // @ts-ignore
            .setFilter( filters.positions );

        headers
            .find( col => col.id === 'team' )
            // @ts-ignore
            .setFilter( filters.teams );

    }, [ filters, headerGroups ] );

    // ===== Render ========================================================= //

    return (
        <div
            ref={ tableRef }
            className={ classNames( 'stats-leaders-table', {
                'stats-leaders-table--is-scrollable': isTableScrollable
            } ) }
        >
            <div
                ref={ scrollWrapperRef }
                className="stats-leaders-table__wrapper js-stats-leaders-table-wrapper"
                // @ts-ignore
                onScroll={ _.debounce( () => {
                    setScrollPosition(
                        scrollWrapperRef.current.scrollLeft
                    );
                }, SCROLL_DEBOUNCE_MS ) }
            >
                {isTableScrollable && (
                    <TableScrollButtons
                        scrollWrapperRef={ scrollWrapperRef }
                        scrollPosition={ scrollPosition }
                    />
                ) }

                <table
                    { ...getTableProps() }
                    className="stats-leaders-table__table"
                >
                    <TableCaption
                        sortedColumnId={ sortedColumn.id }
                        sortDirection={ sortDirection }
                    />
                    
                    <TableHead headerGroup={ headerGroup } />

                    <TableBody
                        preFilteredRows={ preFilteredRows }
                        page={ page }
                        prepareRow={ prepareRow }
                        getTableBodyProps={ getTableBodyProps }
                        players={players}
                    />
                </table>
            </div>

            <NextPageButton
                canNextPage={ canNextPage }
                currentPageSize={ pageSize }
                pageSize={ PAGE_SIZE }
                totalRowCount={ rows.length }
                setPageSize={ setPageSize }
            />
        </div>
    );
};

Table.propTypes = {
    columns: PropTypes.array.isRequired,
    data: PropTypes.array.isRequired,
    players: PropTypes.array.isRequired
};